import { Button, Checkbox, Chip, Dialog, DialogContent, Divider, FormControlLabel, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PickUsers from "./PickUsers";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import { UserRef } from "../../../../models/user";
import PickExportSendBlocks from "../PickExportSendBlocks";

interface Props {
  forceClose: boolean;
  preHandleSend: (
    paySchema: boolean,
    costSchema: boolean,
    sectionsWithZeroAmount: boolean,
    users: UserRef[],
    externalUsers: string[],
    monthlyView: boolean
  ) => Promise<void>;
  allFieldsValidated: boolean;
}

interface State {
  open: boolean;
  CCDOpen: boolean;
  paySchema: boolean;
  costSchema: boolean;
  sectionsWithZeroAmount: boolean;
  users: UserRef[];
  externalUsers: string[];
  monthlyView: boolean;
}

const EmployeeCostSendAction = ({ forceClose, preHandleSend, allFieldsValidated }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.send_employee_cost_report";
  const [state, setState] = useState<State>({
    open: false,
    CCDOpen: false,
    paySchema: true,
    costSchema: true,
    sectionsWithZeroAmount: true,
    users: [],
    externalUsers: [],
    monthlyView: false,
  });

  const handleUpdateExportedBlocks = useCallback(
    (field: "costSchema" | "paySchema" | "sectionsWithZeroAmount", value: boolean) => {
      setState((state) => ({
        ...state,
        [field]: value,
      }));
    },
    []
  );

  const handleUpdateUSers = useCallback((users: UserRef[], externalUsers: string[]) => {
    setState((state) => ({
      ...state,
      users,
      externalUsers,
    }));
  }, []);

  useEffect(() => {
    if (forceClose) setState((state) => ({ ...state, open: false }));
  }, [forceClose]);

  return (
    <>
      <Button
        onClick={() => setState((state) => ({ ...state, open: true }))}
        variant="contained"
        title={!allFieldsValidated ? `${`${t(tRP)}.must_be_validated`}` : "Send to client"}
        disabled={!allFieldsValidated}
        style={{
          pointerEvents: "auto",
        }}
      >
        {`${t("buttons.send")}`}
      </Button>
      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={`${`${t(tRP)}.dialog_title`}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent style={{ display: "flex", gap: "10px", flexDirection: "column" }}>
          <PickExportSendBlocks
            handleUpdateParent={handleUpdateExportedBlocks}
            paySchema={state.paySchema}
            costSchema={state.costSchema}
            sectionsWithZeroAmount={state.sectionsWithZeroAmount}
          />
          <FormControlLabel
            label={
              <Typography>
                {`${`${t(tRP)}.send_monthly_amount`}`}
                <Typography variant="body2">({`${`${t(tRP)}.all_amounts_will_be_divided_by`}`})</Typography>
              </Typography>
            }
            control={
              <Checkbox
                checked={state.monthlyView}
                onChange={(_, checked) => setState((state) => ({ ...state, monthlyView: checked }))}
              />
            }
          />

          <Divider orientation={"horizontal"}>
            <Chip
              label={`${`${t(tRP)}.send_to`}`}
              style={{ color: theme.palette.secondary.light, fontSize: "12px" }}
            />
          </Divider>

          <PickUsers
            handleUpdateUsers={handleUpdateUSers}
            users={state.users}
            externalUsers={state.externalUsers}
          />
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={() =>
            preHandleSend(
              state.paySchema,
              state.costSchema,
              state.sectionsWithZeroAmount,
              state.users,
              state.externalUsers,
              state.monthlyView
            )
          }
          customSaveName={`${t("buttons.send")}`}
          disabledSaveButton={state.users.length === 0 && state.externalUsers.length === 0}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState((state) => ({ ...state, open: false, CCDOpen: false }))}
      />
    </>
  );
};

export default EmployeeCostSendAction;
